import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Toast from '../components/Toast';
import { 
    Eye, EyeOff, Mail, Lock, User, 
    ArrowLeft, AlertCircle, Loader, CheckCircle, 
    Users, Star 
} from 'lucide-react';
import api from '../services/api';
import { getSafeRedirectPath } from '../utils/redirect';

const Register = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { register, loading, error, clearError, isAuthenticated } = useAuth();

    // Form state
    const [formData, setFormData] = useState({
        email: '',
        password: '',
        confirmPassword: '',
        username: '',
        displayName: '',
    });
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [localLoading, setLocalLoading] = useState(false);
    const [passwordStrength, setPasswordStrength] = useState(0);
    const [toast, setToast] = useState({ show: false, message: '', type: 'error' });
    const [siteLogo, setSiteLogo] = useState(null);

    // Clear errors when component mounts
    useEffect(() => {
        clearError();
    }, [clearError]);

    // Watch for errors from auth context
    useEffect(() => {
        if (error) {
            setToast({ show: true, message: error, type: 'error' });
            clearError(); // Clear the error after showing it
        }
    }, [error, clearError]);

    // Redirect if already authenticated
    useEffect(() => {
        if (isAuthenticated) {
            navigate(getSafeRedirectPath(searchParams));
        }
    }, [isAuthenticated, navigate, searchParams]);

    // Fetch site logo
    useEffect(() => {
        const fetchSiteLogo = async () => {
            try {
                const response = await api.get('/admin/settings/public');
                if (response.data.success) {
                    const logoUrl = response.data.data.site_logo;
                    if (logoUrl) {
                        setSiteLogo(logoUrl);
                    }
                }
            } catch (error) {
                console.error('Error fetching site logo:', error);
            }
        };

        fetchSiteLogo();
    }, []);

    // Calculate password strength
    useEffect(() => {
        const password = formData.password;
        let strength = 0;
        
        if (password.length >= 8) strength++;
        if (password.match(/[a-z]/)) strength++;
        if (password.match(/[A-Z]/)) strength++;
        if (password.match(/[0-9]/)) strength++;
        if (password.match(/[^a-zA-Z0-9]/)) strength++;

        setPasswordStrength(strength);
    }, [formData.password]);

    // Handle form input changes
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };


    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();
        setLocalLoading(true);
        clearError();

        // Validation
        if (!formData.email || !formData.password || !formData.username || !formData.displayName) {
            setLocalLoading(false);
            return;
        }

        if (formData.password !== formData.confirmPassword) {
            setLocalLoading(false);
            return;
        }

        if (passwordStrength < 3) {
            setLocalLoading(false);
            return;
        }

        try {
            const result = await register({
                email: formData.email.trim(),
                password: formData.password,
                username: formData.username.trim(),
                displayName: formData.displayName.trim()
            });

            if (result && result.success) {
                setToast({ show: true, message: 'Account created successfully!', type: 'success' });
                setTimeout(() => navigate(getSafeRedirectPath(searchParams)), 1000);
            } else {
                // Get error from result or from auth context
                const errorMsg = result?.error || error || 'Registration failed';
                setToast({ show: true, message: errorMsg, type: 'error' });
            }
        } catch (error) {
            console.error('Registration error:', error);
            setToast({ show: true, message: error.message || 'Registration failed', type: 'error' });
        } finally {
            setLocalLoading(false);
        }
    };

    const isFormLoading = loading || localLoading;
    const passwordsMatch = formData.password === formData.confirmPassword || formData.confirmPassword === '';
    const isFormValid = formData.email && formData.password && formData.username && 
                       formData.displayName && passwordsMatch && passwordStrength >= 3;

    // Password strength indicator
    const getPasswordStrengthColor = () => {
        if (passwordStrength <= 2) return 'bg-red-500';
        if (passwordStrength <= 3) return 'bg-yellow-500';
        return 'bg-green-500';
    };

    const getPasswordStrengthText = () => {
        if (passwordStrength <= 2) return 'Weak';
        if (passwordStrength <= 3) return 'Medium';
        return 'Strong';
    };

    return (
        <div className="min-h-screen bg-hero flex items-center justify-center px-6 py-12">
            {/* Background Elements */}
            <div className="absolute inset-0 overflow-hidden">
                <div className="absolute top-20 left-10 w-72 h-72 bg-purple-500/10 rounded-full blur-3xl animate-pulse-slow"></div>
                <div className="absolute bottom-20 right-10 w-96 h-96 bg-pink-500/10 rounded-full blur-3xl animate-pulse-slow delay-200"></div>
            </div>

            <div className="relative z-10 w-full max-w-lg">
                {/* Header */}
                <div className="text-center mb-8">
                    <Link to="/" className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors duration-300 mb-6">
                        <ArrowLeft className="w-5 h-5" />
                        Back to Home
                    </Link>
                    
                    {siteLogo ? (
                        <div className="mx-auto mb-4">
                            <img
                                src={siteLogo}
                                alt="The Deep Wave Logo"
                                width="180"
                                className="object-cover rounded-lg mx-auto block"
                                style={{
                                    maxWidth: '180px',
                                    width: '180px',
                                    minWidth: 'auto'
                                }}
                            />
                        </div>
                    ) : (
                        <div className="text-6xl mb-4">🌊</div>
                    )}
                    <h1 className="text-4xl font-black gradient-text mb-2">
                        Join The Wave
                    </h1>
                    <p className="text-gray-400">
                        Create your account and connect with the music community
                    </p>
                </div>

                {/* Registration Form */}
                <div className="glass-card p-8">
                    <form onSubmit={handleSubmit} className="space-y-6" autoComplete="off">
                        {/* Error Message */}
                        {error && (
                            <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 flex items-center space-x-3">
                                <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
                                <p className="text-red-400 text-sm">{error}</p>
                            </div>
                        )}


                        {/* Email Field */}
                        <div>
                            <label htmlFor="email" className="block text-sm font-semibold text-gray-300 mb-2">
                                Email
                            </label>
                            <div className="relative">
                                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                                <input
                                    type="email"
                                    id="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    className="input-field pl-12"
                                    placeholder="your@email.com"
                                    autoComplete="new-email"
                                    required
                                    disabled={isFormLoading}
                                />
                            </div>
                        </div>

                        {/* Username Field */}
                        <div>
                            <label htmlFor="username" className="block text-sm font-semibold text-gray-300 mb-2">
                                Username
                            </label>
                            <div className="relative">
                                <Users className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                                <input
                                    type="text"
                                    id="username"
                                    name="username"
                                    value={formData.username}
                                    onChange={handleChange}
                                    className="input-field pl-12"
                                    placeholder="your_username"
                                    pattern="[a-zA-Z0-9_]{3,30}"
                                    title="3-30 characters, letters, numbers, and underscores only"
                                    autoComplete="username"
                                    required
                                    disabled={isFormLoading}
                                />
                            </div>
                            <p className="text-xs text-gray-500 mt-1">
                                3-30 characters, letters, numbers, and underscores only
                            </p>
                        </div>

                        {/* Display Name Field */}
                        <div>
                            <label htmlFor="displayName" className="block text-sm font-semibold text-gray-300 mb-2">
                                Display Name
                            </label>
                            <div className="relative">
                                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                                <input
                                    type="text"
                                    id="displayName"
                                    name="displayName"
                                    value={formData.displayName}
                                    onChange={handleChange}
                                    className="input-field pl-12"
                                    placeholder="Your Name"
                                    maxLength="100"
                                    required
                                    disabled={isFormLoading}
                                />
                            </div>
                        </div>

                        {/* Password Field */}
                        <div>
                            <label htmlFor="password" className="block text-sm font-semibold text-gray-300 mb-2">
                                Password
                            </label>
                            <div className="relative">
                                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    id="password"
                                    name="password"
                                    value={formData.password}
                                    onChange={handleChange}
                                    className="input-field pl-12 pr-12"
                                    placeholder="Create a secure password"
                                    autoComplete="new-password"
                                    required
                                    disabled={isFormLoading}
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white transition-colors duration-300"
                                    disabled={isFormLoading}
                                >
                                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                </button>
                            </div>
                            {/* Password Strength Indicator */}
                            {formData.password && (
                                <div className="mt-2">
                                    <div className="flex items-center gap-2 text-xs">
                                        <span className="text-gray-400">Strength:</span>
                                        <div className="flex-1 bg-gray-700 rounded-full h-2">
                                            <div 
                                                className={`h-full rounded-full transition-all duration-300 ${getPasswordStrengthColor()}`}
                                                style={{ width: `${(passwordStrength / 5) * 100}%` }}
                                            ></div>
                                        </div>
                                        <span className={passwordStrength >= 3 ? 'text-green-400' : 'text-yellow-400'}>
                                            {getPasswordStrengthText()}
                                        </span>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Confirm Password Field */}
                        <div>
                            <label htmlFor="confirmPassword" className="block text-sm font-semibold text-gray-300 mb-2">
                                Confirm Password
                            </label>
                            <div className="relative">
                                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                                <input
                                    type={showConfirmPassword ? 'text' : 'password'}
                                    id="confirmPassword"
                                    name="confirmPassword"
                                    value={formData.confirmPassword}
                                    onChange={handleChange}
                                    className={`input-field pl-12 pr-12 ${
                                        formData.confirmPassword && !passwordsMatch ? 'border-red-500' : ''
                                    }`}
                                    placeholder="Confirm your password"
                                    autoComplete="new-password"
                                    required
                                    disabled={isFormLoading}
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white transition-colors duration-300"
                                    disabled={isFormLoading}
                                >
                                    {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                </button>
                            </div>
                            {formData.confirmPassword && !passwordsMatch && (
                                <p className="text-red-400 text-xs mt-1">Passwords do not match</p>
                            )}
                            {formData.confirmPassword && passwordsMatch && formData.confirmPassword !== '' && (
                                <p className="text-green-400 text-xs mt-1 flex items-center gap-1">
                                    <CheckCircle className="w-3 h-3" />
                                    Passwords match
                                </p>
                            )}
                        </div>

                        {/* Submit Button */}
                        <button
                            type="submit"
                            disabled={isFormLoading || !isFormValid}
                            className="w-full btn-primary flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isFormLoading ? (
                                <>
                                    <Loader className="w-5 h-5 animate-spin" />
                                    Creating Account...
                                </>
                            ) : (
                                <>
                                    <Star className="w-5 h-5" />
                                    Create Account
                                </>
                            )}
                        </button>
                    </form>

                    {/* Divider */}
                    <div className="my-8 flex items-center">
                        <div className="flex-1 border-t border-white/10"></div>
                        <span className="px-4 text-gray-500 text-sm">or</span>
                        <div className="flex-1 border-t border-white/10"></div>
                    </div>

                    {/* Login Link */}
                    <div className="text-center">
                        <p className="text-gray-400 mb-4">
                            Already have an account?
                        </p>
                        <Link 
                            to="/login" 
                            className="btn-secondary w-full flex items-center justify-center gap-3"
                        >
                            Sign In
                        </Link>
                    </div>
                </div>

                {/* Additional Links */}
                <div className="text-center mt-8 text-sm text-gray-500">
                    <p className="mb-4">
                        By creating an account, you agree to our{' '}
                        <Link to="/terms" className="text-purple-400 hover:text-purple-300 transition-colors duration-300">
                            Terms of Service
                        </Link>{' '}
                        and{' '}
                        <Link to="/privacy" className="text-purple-400 hover:text-purple-300 transition-colors duration-300">
                            Privacy Policy
                        </Link>.
                    </p>
                    <div className="flex justify-center space-x-6">
                        <Link to="/support" className="hover:text-purple-400 transition-colors duration-300">
                            Support
                        </Link>
                        <Link to="/about" className="hover:text-purple-400 transition-colors duration-300">
                            About
                        </Link>
                    </div>
                </div>
            </div>
            
            {/* Toast Notification */}
            <Toast
                message={toast.message}
                type={toast.type}
                isVisible={toast.show}
                onClose={() => setToast({ ...toast, show: false })}
            />
        </div>
    );
};

export default Register;